import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { VerifyingOverlay } from '../../components/ui/VerifyingOverlay';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { CertificateRequestForm } from '../../components/forms/CertificateRequestForm';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import {
  approveCertificateRequest,
  enrichCertificateRequestSignatures,
  fetchCertificateRequestSubmission,
  getCertificateRequestAttachmentSignedUrl,
  isCtrApproved,
  isCtrCancelled,
  normalizeCertificateRequestFormData,
  rejectCertificateRequest,
  startCertificateRequestProcessing,
  submitCertificateRequestForApproval,
  updateCertificateRequestAuthorised,
  validateCertificateRequestForm,
} from '../../lib/certificateRequestSubmissions';
import {
  formatCertificateRequestNotifySummary,
  notifyCertificateRequestDecision,
  notifyCertificateRequestPendingApproval,
  notifyCertificateRequestReturned,
} from '../../lib/certificateRequestNotifications';
import { fetchMyCertificateRequestCapabilities } from '../../lib/certificateRequestRoles';
import {
  downloadCertificateRequestExcel,
  downloadCertificateRequestPdf,
} from '../../lib/certificateRequestExcel';
import {
  getCertificateSignedUrl,
  listCertificateFiles,
  resolveCertificateDownloads,
  type CertificateDownloadEntry,
} from '../../lib/certificateLibrary';
import { canAccessCertificateRequest } from '../../lib/applicationFormWorkflow';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { formatSubmittedAt, statusClass, STATUS_LABEL } from '../../lib/accountCreationUi';
import { formatDisplayPersonName } from '../../lib/formatPersonName';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import {
  EMPTY_CERTIFICATE_REQUEST_FORM,
  type ApplicationSubmission,
  type CertificateRequestAttachmentRef,
  type CertificateRequestFormData,
} from '../../types/applicationForm';
import type { CertificateRequestNewNavigateState } from './CertificateRequestNewPage';

function personLabel(raw: string | null | undefined): string {
  return formatDisplayPersonName(raw) || String(raw ?? '').trim();
}

function statusMessage(entry: CertificateDownloadEntry): string {
  if (entry.status === 'ready') return 'Ready';
  if (entry.status === 'expired') {
    return 'Expired — ask IT to upload updated certificate';
  }
  if (entry.status === 'not_available') {
    return entry.unavailable_reason
      ? `Not available — ${entry.unavailable_reason}`
      : 'Not available';
  }
  return 'Missing from library — ask IT to upload';
}

function formatPdfConvertNotice(elapsedSec: number): string {
  return elapsedSec <= 0 ? 'Converting PDF…' : `Converting PDF… (${elapsedSec}s)`;
}

export function CertificateRequestDetailPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = usePortal();
  const { profile, authUser, portalAccess, loading: authLoading } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();

  const homePath = basePath || '/forms';
  const listPath = basePath ? `${basePath}/certificate-request` : '/certificate-request';

  const actorLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  const allowed = canAccessCertificateRequest({
    elevatedDepartment: portalAccess?.elevatedDepartment,
    departmentSlug: portalAccess?.departmentSlug,
    formsRole: portalAccess?.forms.profile?.role,
  });

  const [row, setRow] = useState<ApplicationSubmission | null>(null);
  const [form, setForm] = useState<CertificateRequestFormData>(EMPTY_CERTIFICATE_REQUEST_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pdfOverlay, setPdfOverlay] = useState<string | null>(null);
  const [isAuthorised, setIsAuthorised] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [startedProcessing, setStartedProcessing] = useState(false);
  const [certDownloads, setCertDownloads] = useState<CertificateDownloadEntry[]>([]);
  const [certBusyKey, setCertBusyKey] = useState<string | null>(null);
  const pdfTimerRef = useRef<number | null>(null);
  const lastErrorToastRef = useRef<string | null>(null);
  const lastNoticeToastRef = useRef<string | null>(null);
  const shownStatusTipsRef = useRef<Set<string>>(new Set());
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  const actorEmail = authUser?.email ?? profile?.email ?? null;

  const stopPdfTimer = useCallback(() => {
    if (pdfTimerRef.current != null) {
      window.clearInterval(pdfTimerRef.current);
      pdfTimerRef.current = null;
    }
  }, []);

  const clearPdfOverlay = useCallback(() => {
    stopPdfTimer();
    setPdfOverlay(null);
  }, [stopPdfTimer]);

  useEffect(() => () => clearPdfOverlay(), [clearPdfOverlay]);

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  useEffect(() => {
    if (notice && notice !== lastNoticeToastRef.current) {
      lastNoticeToastRef.current = notice;
      pushToast(pageFeedbackToToast({ kind: 'success', message: notice }));
    }
    if (!notice) lastNoticeToastRef.current = null;
  }, [notice, pushToast]);

  useEffect(() => {
    const flash = (location.state ?? null) as CertificateRequestNewNavigateState | null;
    if (!flash?.ctrNotice && !flash?.ctrError) return;
    if (flash.ctrNotice) setNotice(flash.ctrNotice);
    if (flash.ctrError) setError(flash.ctrError);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const applyLoaded = (data: ApplicationSubmission) => {
    setRow(data);
    setForm(normalizeCertificateRequestFormData(data.form_data));
  };

  const refreshCertificateDownloads = useCallback(async (formData: CertificateRequestFormData) => {
    try {
      const library = await listCertificateFiles({ activeOnly: true });
      setCertDownloads(resolveCertificateDownloads(formData, library));
    } catch {
      setCertDownloads([]);
    }
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [data, caps] = await Promise.all([
        fetchCertificateRequestSubmission(id),
        fetchMyCertificateRequestCapabilities(actorEmail),
      ]);
      applyLoaded(data);
      setIsAuthorised(caps.isAuthorised);
      setIsApprover(caps.isApprover);
      // Library certificates: Authorised/Approver before approve; requestor only after approved.
      const approved = isCtrApproved(data.status);
      const rejected = data.status === 'rejected';
      const cancelled = isCtrCancelled(data.status);
      const roleCanSeeCerts = caps.isAuthorised || caps.isApprover;
      const requestorCanSeeCerts = approved && !rejected;
      if (!cancelled && (roleCanSeeCerts || requestorCanSeeCerts)) {
        await refreshCertificateDownloads(data.form_data as CertificateRequestFormData);
      } else {
        setCertDownloads([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load.');
      setRow(null);
      setCertDownloads([]);
    } finally {
      setLoading(false);
    }
  }, [id, actorEmail, refreshCertificateDownloads]);

  useEffect(() => {
    if (authLoading || !allowed) return;
    void load();
  }, [authLoading, allowed, load]);

  useFormsSubmissionsRealtime({
    enabled: Boolean(id) && !authLoading && allowed,
    tables: ['certificate_request_submissions'],
    filter: id ? `id=eq.${id}` : undefined,
    onChange: () => {
      void load();
    },
  });

  // Authorised user opening a New request → Processing
  useEffect(() => {
    if (!row || !isAuthorised || startedProcessing) return;
    if (row.status !== 'new') return;
    let cancelled = false;
    setStartedProcessing(true);
    void (async () => {
      try {
        const updated = await startCertificateRequestProcessing(row.id);
        if (!cancelled) applyLoaded(updated);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to start processing.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row, isAuthorised, startedProcessing]);

  const status = row?.status;
  const isCancelled = isCtrCancelled(status);
  const isApproved = isCtrApproved(status);
  const isRejected = status === 'rejected';
  const authorisedEditable =
    isAuthorised && !isCancelled && (status === 'new' || status === 'processing');
  const canSubmitToApprover =
    isAuthorised && !isCancelled && (status === 'new' || status === 'processing');
  const canApproverReturn = isApprover && status === 'pending_approval';
  const canAuthorisedReject =
    isAuthorised &&
    (status === 'new' ||
      status === 'processing' ||
      (status === 'pending_approval' && !isApprover));
  const canApprove = isApprover && status === 'pending_approval';
  const fieldsLocked = isCancelled || isApproved || !authorisedEditable;
  /**
   * Library certificate downloads only (Excel/PDF stay visible to everyone):
   * - After submit: Authorised + Approver only
   * - After approved: requestor can download too
   * - Rejected / cancelled: hidden from requestor
   */
  const canSeeCertificateDownloads =
    !isCancelled &&
    ((isAuthorised || isApprover) || (isApproved && !isRejected));

  const isReturnedByApprover =
    status === 'processing' && Boolean(row?.rejected_by || row?.reject_reason);

  useEffect(() => {
    if (!row) return;
    let tip: { kind: 'info' | 'error'; title: string; message: string } | null = null;
    if (isReturnedByApprover) {
      tip = {
        kind: 'info',
        title: 'Returned by Approver',
        message: `${row.rejected_by ? `By ${personLabel(row.rejected_by)}. ` : ''}${
          row.reject_reason ? `${row.reject_reason} ` : ''
        }Authorised users can edit and submit again.`,
      };
    } else if (status === 'rejected' && (row.reject_reason || row.rejected_by)) {
      tip = {
        kind: 'error',
        title: 'Rejected',
        message: `${row.rejected_by ? `By ${personLabel(row.rejected_by)}. ` : ''}${
          row.reject_reason || 'This certificate request was rejected.'
        }`,
      };
    } else if (isApproved) {
      tip = {
        kind: 'info',
        title: 'Approved',
        message: 'View only. Certificate downloads are below.',
      };
    } else if (isCancelled) {
      tip = {
        kind: 'info',
        title: 'Cancelled',
        message: 'This request is cancelled and cannot be edited.',
      };
    } else if (status === 'pending_approval') {
      tip = {
        kind: 'info',
        title: 'Pending approval',
        message: 'Waiting for Approver decision.',
      };
    }
    if (!tip) return;
    const key = `${row.id}:${tip.title}:${row.reject_reason ?? ''}:${row.rejected_at ?? ''}`;
    if (shownStatusTipsRef.current.has(key)) return;
    shownStatusTipsRef.current.add(key);
    pushToast(pageFeedbackToToast(tip));
  }, [row, status, isReturnedByApprover, isApproved, isCancelled, pushToast]);

  const workflowItems = useMemo(() => {
    if (!row) return [] as { label: string; name: string; at: string | null }[];
    const items: { label: string; name: string; at: string | null }[] = [];
    const submitter = personLabel(row.submitted_by) || personLabel(row.submitter_email);
    if (submitter) {
      items.push({ label: 'Requested by', name: submitter, at: row.submitted_at });
    }
    if (row.authorised_by) {
      items.push({
        label: 'Prepared by',
        name: personLabel(row.authorised_by),
        at: row.authorised_at ?? null,
      });
    }
    if (row.approved_by) {
      items.push({
        label: 'Verified by',
        name: personLabel(row.approved_by),
        at: row.approved_at ?? null,
      });
    }
    if (row.rejected_by && (status === 'rejected' || isReturnedByApprover)) {
      items.push({
        label: status === 'rejected' ? 'Rejected by' : 'Returned by',
        name: personLabel(row.rejected_by),
        at: row.rejected_at ?? null,
      });
    }
    return items;
  }, [row, status, isReturnedByApprover]);

  const startPdfOverlay = useCallback(() => {
    clearPdfOverlay();
    const startedAt = Date.now();
    setPdfOverlay(formatPdfConvertNotice(0));
    pdfTimerRef.current = window.setInterval(() => {
      const elapsedSec = Math.floor((Date.now() - startedAt) / 1000);
      setPdfOverlay(formatPdfConvertNotice(elapsedSec));
    }, 1000);
  }, [clearPdfOverlay]);

  if (authLoading) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Checking access…</p>
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to={homePath} replace />;
  }

  const optionalDecisionNote = (actionLabel: string): string => {
    const note = window.prompt(`Optional note for ${actionLabel} (leave blank to continue):`);
    if (note === null) return '__cancelled__';
    return note.trim();
  };

  const handleOpenAttachment = async (ref: CertificateRequestAttachmentRef) => {
    setError(null);
    try {
      const url = await getCertificateRequestAttachmentSignedUrl(ref.path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open attachment.');
    }
  };

  const formWithSignatures = (
    overrides?: Partial<{ prepared_by: string; verified_by: string }>,
  ): CertificateRequestFormData => {
    if (!row) return form;
    return enrichCertificateRequestSignatures(form, row, {
      prepared_by: overrides?.prepared_by,
      verified_by: overrides?.verified_by,
    });
  };

  const handleSave = async () => {
    if (!row || !authorisedEditable) return;
    const validationError = validateCertificateRequestForm(form, {
      requireCustomerAttachments: false,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = formWithSignatures({ prepared_by: actorLabel });
      const updated = await updateCertificateRequestAuthorised(row.id, payload);
      applyLoaded(updated);
      setNotice('Saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitToApprover = async () => {
    if (!row || !canSubmitToApprover) return;
    const validationError = validateCertificateRequestForm(form, {
      requireCustomerAttachments: false,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!window.confirm('Submit this certificate request to the approver?')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = formWithSignatures({ prepared_by: actorLabel });
      const updated = await submitCertificateRequestForApproval(row.id, payload);
      applyLoaded(updated);
      const notifyResult = await notifyCertificateRequestPendingApproval(updated);
      const summary = await formatCertificateRequestNotifySummary(notifyResult);
      setNotice(`Submitted for approval. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(
          'Check Approver emails in Forms settings → Certificate Request so they get notified.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit for approval.');
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!row || !canApprove) return;
    if (!window.confirm('Approve this certificate request? A PDF will download.')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = formWithSignatures({
        prepared_by: form.prepared_by.trim() || personLabel(row.authorised_by) || actorLabel,
        verified_by: actorLabel,
      });
      const updated = await approveCertificateRequest(row.id, payload);
      applyLoaded(updated);
      await refreshCertificateDownloads(updated.form_data as CertificateRequestFormData);
      const stampedForm = enrichCertificateRequestSignatures(
        normalizeCertificateRequestFormData(updated.form_data),
        updated,
        { verified_by: actorLabel },
      );
      startPdfOverlay();
      try {
        await downloadCertificateRequestPdf(stampedForm, updated.application_no);
      } finally {
        clearPdfOverlay();
      }
      const notifyResult = await notifyCertificateRequestDecision(updated, 'approved');
      const summary = await formatCertificateRequestNotifySummary(notifyResult);
      setNotice(`Approved. PDF downloaded. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(
          'The submitter may not be notified until their email is on the submission.',
        );
      }
    } catch (err) {
      clearPdfOverlay();
      setError(err instanceof Error ? err.message : 'Failed to approve.');
    } finally {
      clearPdfOverlay();
      setBusy(false);
    }
  };

  const handleApproverReturn = async () => {
    if (!row || !canApproverReturn) return;
    if (
      !window.confirm(
        'Return this request to Authorised users for changes? They can edit and submit again.',
      )
    ) {
      return;
    }
    const note = optionalDecisionNote('return');
    if (note === '__cancelled__') return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await rejectCertificateRequest(row.id, note || 'Returned', form);
      applyLoaded(updated);
      const notifyResult = await notifyCertificateRequestReturned(updated);
      const summary = await formatCertificateRequestNotifySummary(notifyResult);
      setNotice(`Returned to Authorised users. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(summary);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to return request.');
    } finally {
      setBusy(false);
    }
  };

  const handleAuthorisedReject = async () => {
    if (!row || !canAuthorisedReject) return;
    if (!window.confirm('Reject this certificate request permanently?')) return;
    const note = optionalDecisionNote('reject');
    if (note === '__cancelled__') return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = formWithSignatures(
        authorisedEditable ? { prepared_by: actorLabel } : undefined,
      );
      if (authorisedEditable) {
        await updateCertificateRequestAuthorised(row.id, payload);
      }
      const updated = await rejectCertificateRequest(row.id, note || 'Rejected', payload);
      applyLoaded(updated);
      const notifyResult = await notifyCertificateRequestDecision(updated, 'rejected');
      const summary = await formatCertificateRequestNotifySummary(notifyResult);
      setNotice(`Rejected. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(
          'The submitter may not be notified until their email is on the submission.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject.');
    } finally {
      setBusy(false);
    }
  };

  const handleExcel = async () => {
    if (!row) return;
    const validationError = validateCertificateRequestForm(form, {
      requireCustomerAttachments: false,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = formWithSignatures(
        authorisedEditable ? { prepared_by: actorLabel } : undefined,
      );
      if (authorisedEditable) {
        const updated = await updateCertificateRequestAuthorised(row.id, payload);
        applyLoaded(updated);
        await downloadCertificateRequestExcel(
          enrichCertificateRequestSignatures(
            normalizeCertificateRequestFormData(updated.form_data),
            updated,
          ),
          updated.application_no,
        );
      } else {
        await downloadCertificateRequestExcel(payload, row.application_no);
      }
      setNotice('Excel downloaded.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download Excel.');
    } finally {
      setBusy(false);
    }
  };

  const handlePdf = async () => {
    if (!row) return;
    const validationError = validateCertificateRequestForm(form, {
      requireCustomerAttachments: false,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    startPdfOverlay();
    try {
      const payload = formWithSignatures(
        authorisedEditable ? { prepared_by: actorLabel } : undefined,
      );
      if (authorisedEditable) {
        const updated = await updateCertificateRequestAuthorised(row.id, payload);
        applyLoaded(updated);
        await downloadCertificateRequestPdf(
          enrichCertificateRequestSignatures(
            normalizeCertificateRequestFormData(updated.form_data),
            updated,
          ),
          updated.application_no,
        );
      } else {
        await downloadCertificateRequestPdf(payload, row.application_no);
      }
      setNotice('PDF downloaded.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download PDF.');
    } finally {
      clearPdfOverlay();
      setBusy(false);
    }
  };

  const handleCertificateDownload = async (entry: CertificateDownloadEntry) => {
    if (entry.status !== 'ready' || !entry.storage_path) return;
    setCertBusyKey(entry.key);
    setError(null);
    try {
      const url = await getCertificateSignedUrl(entry.storage_path);
      const a = document.createElement('a');
      a.href = url;
      a.download = entry.file_name || 'certificate.pdf';
      a.rel = 'noopener';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download certificate.');
    } finally {
      setCertBusyKey(null);
    }
  };

  if (loading) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Loading…</p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="af-applications-page af-form-page">
        {activeToast
          ? createPortal(
              <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
              document.body,
            )
          : null}
        <PageHeader
          title="Certificate Request"
          description={error ?? 'Not found.'}
          actions={
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="af-applications-page af-form-page">
      <PageHeader
        title={`${row.application_no} - ${STATUS_LABEL[row.status] ?? row.status}`}
        description={`${row.company_name} · ${formatSubmittedAt(row.submitted_at)}`}
        actions={
          <div className="af-applications-page__actions">
            <span className={statusClass(row.status)}>{STATUS_LABEL[row.status] ?? row.status}</span>
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          </div>
        }
      />

      {pdfOverlay ? <VerifyingOverlay message={pdfOverlay} /> : null}
      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <div className="puf-detail-layout">
        <div className="puf-detail-main">
          <div className="card ctr-form-card">
            <CertificateRequestForm
              value={form}
              onChange={setForm}
              applicationNo={row.application_no}
              disabled={fieldsLocked}
              onOpenAttachment={(ref) => void handleOpenAttachment(ref)}
            />
            <div className="ctr-form__actions ctr-form__actions--split">
              <div className="ctr-form__actions-group">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => void handleExcel()}
                >
                  Download Excel
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => void handlePdf()}
                >
                  {busy ? 'Working…' : 'Download PDF'}
                </button>
              </div>
              <div className="ctr-form__actions-group">
                {authorisedEditable ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => void handleSave()}
                  >
                    {busy ? 'Working…' : 'Save'}
                  </button>
                ) : null}
                {canSubmitToApprover ? (
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={busy}
                    onClick={() => void handleSubmitToApprover()}
                  >
                    {busy ? 'Working…' : 'Submit to Approver'}
                  </button>
                ) : null}
                {canApprove ? (
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={busy}
                    onClick={() => void handleApprove()}
                  >
                    {busy ? 'Working…' : 'Approve'}
                  </button>
                ) : null}
                {canApproverReturn ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => void handleApproverReturn()}
                  >
                    {busy ? 'Working…' : 'Return to Authorised'}
                  </button>
                ) : null}
                {canAuthorisedReject ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() => void handleAuthorisedReject()}
                  >
                    {busy ? 'Working…' : 'Reject'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {canSeeCertificateDownloads && certDownloads.length > 0 ? (
            <div className="card ctr-form-card ctr-cert-downloads">
              <h2 className="ctr-cert-downloads__title">Certificate downloads</h2>
              <p className="ctr-cert-downloads__lead">
                {isApproved
                  ? 'Library PDFs for the selected certificate types. Expired or missing files cannot be downloaded until Purchasing updates the library.'
                  : 'Visible to Authorised users and Approvers only until this request is approved. Requestors can download certificates after approval.'}
              </p>
              <ul className="ctr-cert-downloads__list">
                {certDownloads.map((entry) => (
                  <li
                    key={entry.key}
                    className={`ctr-cert-downloads__item ctr-cert-downloads__item--${entry.status}`}
                  >
                    <div className="ctr-cert-downloads__meta">
                      <strong>{entry.label}</strong>
                      {entry.file_name ? <code>{entry.file_name}</code> : null}
                      {entry.status === 'not_available' && entry.unavailable_reason ? (
                        <span className="ctr-cert-downloads__reason">
                          {entry.unavailable_reason}
                        </span>
                      ) : null}
                      <span
                        className={`ctr-cert-lib__pill ctr-cert-lib__pill--${
                          entry.status === 'ready'
                            ? 'ok'
                            : entry.status === 'expired'
                              ? 'expired'
                              : 'missing'
                        }`}
                      >
                        {statusMessage(entry)}
                      </span>
                    </div>
                    {entry.status === 'ready' ? (
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={certBusyKey === entry.key}
                        onClick={() => void handleCertificateDownload(entry)}
                      >
                        {certBusyKey === entry.key ? 'Opening…' : 'Download'}
                      </button>
                    ) : (
                      <span className="ctr-cert-downloads__blocked">
                        {entry.status === 'not_available' ? 'No file' : 'Download blocked'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        {workflowItems.length > 0 ? (
          <aside className="card puf-workflow-meta">
            <h3 className="puf-workflow-meta__title">Workflow</h3>
            <ul className="puf-workflow-meta__list">
              {workflowItems.map((item) => (
                <li key={`${item.label}-${item.name}-${item.at ?? ''}`}>
                  <span className="puf-workflow-meta__label">{item.label}</span>
                  <span className="puf-workflow-meta__name">{item.name}</span>
                  {item.at ? (
                    <span className="puf-workflow-meta__time">{formatSubmittedAt(item.at)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
